import { useEffect, useRef, useState } from 'react';
import type { AppUser } from '@kawai/shared';
import { fetchAllUsers } from '../repositories/users.js';

export type AllUsersStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface UseAllUsersResult {
  status: AllUsersStatus;
  users: AppUser[];
}

/**
 * N-15b: 自テナントの全顧客を 1 回読む hook（LINE 宛先ピッカーの検索母集団）。
 * tenant-portal は React Query 非依存のため `useBillingCharges` と同じ useEffect パターン。
 * `enabled: false` の間は読まない（ピッカーを開くまで users を読まない）。
 */
export function useAllUsers(
  tenantId: string | null,
  enabled: boolean,
  fetcher: (id: string) => Promise<AppUser[]> = fetchAllUsers,
): UseAllUsersResult {
  const fetcherRef = useRef(fetcher);
  const [state, setState] = useState<UseAllUsersResult>({ status: 'idle', users: [] });

  useEffect(() => {
    if (!tenantId || !enabled) return;
    let cancelled = false;
    setState((prev) => ({ status: 'loading', users: prev.users }));
    fetcherRef
      .current(tenantId)
      .then((users) => {
        if (!cancelled) setState({ status: 'ready', users });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error('[allUsers] fetch failed:', err);
        setState({ status: 'error', users: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, enabled]);

  return state;
}
